import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Grid, List, ChevronRight, Zap } from 'lucide-react'
import { Deck, Card, Faction } from '../types'
import { useAppStore } from '../store'
import { CardImage } from '../components/ui/CardImage'

const FACTION_NAMES: Record<Faction, string> = {
  HUMAN: 'Humano',
  DEMON: 'Demonio',
  ELF: 'Elfo',
  DRAGON: 'Dragón',
  UNDEAD: 'No-muerto',
  NEUTRAL: 'Neutral',
}

const USE_RACE_FILTER = false

function getDeckCards(deck: Deck, allCards: Card[]): Card[] {
  const cards: Card[] = []
  deck.cards.forEach(deckCard => {
    const card = allCards.find(c => c.id === deckCard.cardId || c.cardId === deckCard.cardId)
    if (card) {
      for (let i = 0; i < deckCard.quantity; i++) {
        cards.push(card)
      }
    }
  })
  return cards
}

function DeckCard({ deck, topCards }: { deck: Deck; topCards: Card[] }) {
  const navigate = useNavigate()
  const allDeckCards = getDeckCards(deck, topCards)

  const goldCount = allDeckCards.filter(c => c.goldCost && c.goldCost > 0).length
  const avgCost = allDeckCards.length > 0
    ? (allDeckCards.reduce((sum, c) => sum + c.cost, 0) / allDeckCards.length).toFixed(1)
    : '0.0'

  return (
    <div
      onClick={() => navigate(`/deck/${deck.id}`)}
      className="bg-[#1a1e24] rounded-xl border border-[#2d3540] hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/10 transition-all cursor-pointer group overflow-hidden flex flex-col h-full"
    >
      <div className="p-4 flex-1 flex flex-col">
        <div className="mb-3">
          <h3 className="text-lg font-semibold text-white group-hover:text-amber-400 transition-colors">
            {deck.name}
          </h3>
          {deck.description && (
            <p className="text-sm text-slate-400 mt-1 line-clamp-2">{deck.description}</p>
          )}
        </div>

        {topCards.length > 0 && (
          <div className="grid grid-cols-5 gap-2 mb-4">
            {topCards.slice(0, 5).map((card, i) => (
              <div key={i} className="aspect-[2/3]">
                <CardImage card={card} className="w-full h-full object-cover rounded" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 bg-[#0f1115] border-t border-[#2d3540]">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-slate-400">
              <span className="flex items-center gap-1">
                <span className="text-amber-400">💰</span>
                {goldCount}
              </span>
              <span>{allDeckCards.length}/50 cartas</span>
              <span>Costo: {avgCost}</span>
            </div>
            <ChevronRight size={16} className="text-slate-500 group-hover:text-amber-400 transition-colors" />
          </div>
      </div>
    </div>
  )
}

function createRandomDecks(cards: Card[]): Deck[] {
  if (cards.length === 0) return []

  const STORAGE_KEY = 'demo-decks-v1'
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      console.error('Error loading demo decks:', e)
    }
  }

  const formats = ['RACIAL', 'RACIAL_LIBRE', 'RACIAL_LIMITADO', 'RACIAL_EDICION', 'VCR', 'TRIADAS'] as const
  const factions = Object.values({ HUMAN: 'HUMAN', DEMON: 'DEMON', ELF: 'ELF', DRAGON: 'DRAGON', UNDEAD: 'UNDEAD', NEUTRAL: 'NEUTRAL' })

  const goldCards = cards.filter(card => card.goldCost !== undefined && card.goldCost > 0)
  const otherCards = cards.filter(card => card.goldCost === undefined || card.goldCost === 0)

  const deckNames = [
    'Ejército de Caballeros',
    'Dracónido Infinito',
    'Sombra Eterna',
    'Guardianes del Bosque',
    'Liga de Demonios',
    'Fuerza Neutral',
    'Campeones Imperio',
    'Maestros Furia',
  ]

  const deckDescriptions = [
    'Mazo agresivo con énfasis en ataques rápidos',
    'Estrategia de control con dragones poderosos',
    'Mazo de no-muertos con regeneración',
    'Elfos y criaturas mágicas para ganar por ventaja',
    'Demonios con efectos de daño masivo',
    'Equilibrio perfecto con cartas neutrales',
    'Combinación racial para meta competitivo',
    'Mazo experimental con triadas',
  ]

  const decks = deckNames.slice(0, 8).map((name, i) => {
    const faction = factions[i % factions.length]

    const factionGoldCards = goldCards.filter(c => c.faction === faction)
    const factionOtherCards = otherCards.filter(c => c.faction === faction)

    const selectedGoldCards = factionGoldCards.slice(0, 18)
    const selectedOtherCards = factionOtherCards.slice(0, 50 - selectedGoldCards.length)

    const selectedCards = [...selectedGoldCards, ...selectedOtherCards].slice(0, 50)

    const uniqueCards = Array.from(new Map(selectedCards.map(card => [card.id, card])).values())

    const deckCards = uniqueCards.map(card => ({
      cardId: card.id,
      card: card,
      quantity: selectedCards.filter(c => c.id === card.id).length,
    }))

    return {
      id: `demo-${i}`,
      name: name,
      description: deckDescriptions[i],
      format: formats[i % formats.length],
      isPublic: true,
      userId: 'demo',
      cards: deckCards,
      createdAt: new Date(Date.now() - i * 86400000).toISOString(),
      updatedAt: new Date().toISOString(),
    }
  })

  localStorage.setItem(STORAGE_KEY, JSON.stringify(decks))
  return decks
}

interface DeckWithCards extends Deck {
  mainCards: Card[]
  topCards: Card[]
}

export default function DecksPage() {
  const navigate = useNavigate()
  const storeCards = useAppStore((state) => state.cards)
  const cards = Array.isArray(storeCards) ? storeCards : []
  const decks = useAppStore((state) => state.decks)

  const [search, setSearch] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('ALL')
  const [selectedFaction, setSelectedFaction] = useState<Faction | 'ALL'>('ALL')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const demoDecks = useMemo(() => createRandomDecks(cards), [cards])

  const allDecks = [...decks, ...demoDecks]

  const decksWithCards: DeckWithCards[] = useMemo(() => {
    return allDecks.map(deck => ({
      ...deck,
      mainCards: getDeckCards(deck, cards),
      topCards: getDeckCards(deck, cards).slice(0, 8),
    }))
  }, [allDecks, cards])

  const allFactions = Object.values(Faction) as Faction[]

  const filteredDecks = useMemo(() => {
    return decksWithCards.filter(deck => {
      const matchSearch = search === '' || deck.name.toLowerCase().includes(search.toLowerCase())
      const matchFormat = selectedFormat === 'ALL' || deck.format.toUpperCase() === selectedFormat
      const matchFaction = selectedFaction === 'ALL' || deck.mainCards.some(card => card.faction === selectedFaction)
      return matchSearch && matchFormat && matchFaction
    })
  }, [decksWithCards, search, selectedFormat, selectedFaction])

  return (
    <div className="min-h-screen bg-[#0f1115]">
      <div className="bg-[#161920] border-b border-[#232730]">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Explorar Mazos</h1>
              <p className="text-sm text-slate-400 mt-1">
                {allDecks.length} mazos disponibles ({demoDecks.length} de ejemplo)
              </p>
            </div>
            <button
              onClick={() => navigate('/deck/new')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-[#0f1115] font-semibold rounded-lg transition-all"
            >
              <Zap size={18} />
              Crear Mazo
            </button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8b919a]" size={20} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar mazos..."
              className="w-full pl-12 pr-4 py-3 bg-[#1e232a] border border-[#2d3540] rounded-xl text-white placeholder-[#8b919a] focus:outline-none focus:border-[#f59e0b] transition-all"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-[#8b919a]" />
              <label className="text-xs text-[#8b919a] uppercase">Formato</label>
              <select
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white focus:outline-none focus:border-[#f59e0b] cursor-pointer min-w-40"
              >
                <option value="ALL">Todos</option>
                <option value="RACIAL">Racial</option>
                <option value="RACIAL_LIBRE">Racial Libre</option>
                <option value="RACIAL_LIMITADO">Racial Limitado</option>
                <option value="RACIAL_EDICION">Racial Edición</option>
                <option value="VCR">VCR</option>
                <option value="TRIADAS">Triadas</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-[#8b919a] uppercase">Facción</label>
              <select
                value={selectedFaction}
                onChange={(e) => setSelectedFaction(e.target.value as Faction | 'ALL')}
                className="px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white focus:outline-none focus:border-[#f59e0b] cursor-pointer min-w-32"
              >
                <option value="ALL">Todas</option>
                {allFactions.map(faction => (
                  <option key={faction} value={faction}>{FACTION_NAMES[faction] || faction}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-[#2d3540] text-white' : 'text-[#8b919a] hover:text-white'}`}
              >
                <Grid size={18} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-[#2d3540] text-white' : 'text-[#8b919a] hover:text-white'}`}
              >
                <List size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {filteredDecks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <p className="text-[#8b919a]">No se encontraron mazos</p>
            <button
              onClick={() => navigate('/deck/new')}
              className="mt-4 px-4 py-2 bg-[#f59e0b] text-[#0f1115] font-medium rounded-lg hover:bg-[#fbbf24] transition-colors"
            >
              Crear tu primer mazo
            </button>
          </div>
        ) : (
          <>
            <div className={`${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}`}>
              {filteredDecks.map(deck => (
                <DeckCard key={deck.id} deck={deck} topCards={deck.topCards} />
              ))}
            </div>

            <p className="text-center text-sm text-[#8b919a] mt-6">
              {filteredDecks.length} mazos
            </p>
          </>
        )}
      </div>
    </div>
  )
}
